import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  ApplicationDetailResponse,
  CandidateProfile,
  CandidateRow,
  CorrectSourceInput,
  DuplicateFlagView,
  ManualCandidateRequest,
  ManualCandidateResult,
  MergeCandidatesRequest,
  MergePreview,
  MergeResult,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, request } from '../../../lib/api-client';

// S14: CAND-01/02/03/06/07. The API's realtime hints name `['candidates']` and
// `['applications']`, so a colleague's merge or a new application refreshes these.

export const CANDIDATES_KEY = ['candidates'] as const;
export const APPLICATIONS_KEY = ['applications'] as const;
const DUPLICATES_KEY = [...CANDIDATES_KEY, 'duplicates'] as const;

export function candidateKey(id: string): readonly unknown[] {
  return [...CANDIDATES_KEY, 'detail', id];
}

export function applicationKey(id: string): readonly unknown[] {
  return [...APPLICATIONS_KEY, 'detail', id];
}

export interface CandidateListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly query?: string;
}

export function useCandidates(params: CandidateListParams) {
  return useQuery<{ rows: readonly CandidateRow[]; total: number }, ApiError>({
    queryKey: [...CANDIDATES_KEY, 'list', params],
    queryFn: async () => {
      const result = await request<readonly CandidateRow[]>('/candidates', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export function useCandidate(id: string): UseQueryResult<CandidateProfile, ApiError> {
  return useQuery<CandidateProfile, ApiError>({
    queryKey: candidateKey(id),
    queryFn: () => apiGet<CandidateProfile>(`/candidates/${id}`),
  });
}

export function useApplication(id: string): UseQueryResult<ApplicationDetailResponse, ApiError> {
  return useQuery<ApplicationDetailResponse, ApiError>({
    queryKey: applicationKey(id),
    queryFn: () => apiGet<ApplicationDetailResponse>(`/applications/${id}`),
  });
}

export function useDuplicates(): UseQueryResult<readonly DuplicateFlagView[], ApiError> {
  return useQuery<readonly DuplicateFlagView[], ApiError>({
    queryKey: DUPLICATES_KEY,
    queryFn: async () => (await apiGet<{ flags: readonly DuplicateFlagView[] }>('/candidates/duplicates')).flags,
    placeholderData: (previous) => previous,
  });
}

/** N-17 lands on one flag, which may already have been resolved by someone else. */
export function useDuplicateFlag(id: string | null): UseQueryResult<DuplicateFlagView, ApiError> {
  return useQuery<DuplicateFlagView, ApiError>({
    queryKey: [...DUPLICATES_KEY, 'flag', id],
    queryFn: async () => (await apiGet<{ flag: DuplicateFlagView }>(`/candidates/duplicates/${id ?? ''}`)).flag,
    enabled: id !== null,
  });
}

export function useMergePreview(first: string, second: string): UseQueryResult<MergePreview, ApiError> {
  return useQuery<MergePreview, ApiError>({
    queryKey: [...CANDIDATES_KEY, 'merge-preview', first, second],
    queryFn: async () =>
      (await request<{ preview: MergePreview }>('/candidates/merge-preview', { query: { a: first, b: second } })).data.preview,
  });
}

// --- Mutations -----------------------------------------------------------------------------

/**
 * F7.07 / E5 on CAND-06. The flag leaves the queue (or drops to the back) at once;
 * a refusal puts it back and the caller toasts why (§5a rule 1).
 */
export function useFlagAction() {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    ApiError,
    { readonly flag: DuplicateFlagView; readonly action: 'dismiss' | 'defer' },
    { readonly previous: readonly DuplicateFlagView[] | undefined }
  >({
    mutationFn: ({ flag, action }) => apiPost(`/candidates/duplicates/${flag.id}/${action}`, { version: flag.version }),
    onMutate: async ({ flag, action }) => {
      await queryClient.cancelQueries({ queryKey: DUPLICATES_KEY });
      const previous = queryClient.getQueryData<readonly DuplicateFlagView[]>(DUPLICATES_KEY);
      if (previous) {
        const rest = previous.filter((item) => item.id !== flag.id);
        queryClient.setQueryData(DUPLICATES_KEY, action === 'dismiss' ? rest : [...rest, { ...flag, deferredAt: new Date().toISOString() }]);
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(DUPLICATES_KEY, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: DUPLICATES_KEY });
    },
  });
}

export function useMerge() {
  const queryClient = useQueryClient();
  return useMutation<MergeResult, ApiError, MergeCandidatesRequest>({
    mutationFn: (input) => apiPost<MergeResult>('/candidates/merge', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY });
    },
  });
}

export function useUnmerge(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation<MergeResult, ApiError, { readonly mergeId: string; readonly version: number }>({
    mutationFn: ({ mergeId, version }) => apiPost<MergeResult>(`/candidates/merges/${mergeId}/unmerge`, { version }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) });
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
    },
  });
}

/** F7.13 and S14 Q8. The badge moves at once; the server's profile replaces it. */
export function useDoNotRehire(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    CandidateProfile,
    ApiError,
    { readonly isSetting: boolean; readonly reason: string; readonly version: number },
    { readonly previous: CandidateProfile | undefined }
  >({
    mutationFn: ({ isSetting, reason, version }) =>
      apiPost<CandidateProfile>(`/candidates/${candidateId}/do-not-rehire${isSetting ? '' : '/clear'}`, { reason, version }),
    onMutate: async ({ isSetting, reason }) => {
      await queryClient.cancelQueries({ queryKey: candidateKey(candidateId) });
      const previous = queryClient.getQueryData<CandidateProfile>(candidateKey(candidateId));
      if (previous) {
        queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), {
          ...previous,
          candidate: { ...previous.candidate, doNotRehire: isSetting, doNotRehireReason: isSetting ? reason : null },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(candidateKey(candidateId), context.previous);
    },
    onSuccess: async (profile) => {
      queryClient.setQueryData(candidateKey(candidateId), profile);
      await queryClient.invalidateQueries({ queryKey: [...CANDIDATES_KEY, 'list'] });
    },
  });
}

/** S14 Q7: the source label changes at once; the server's detail replaces it. */
export function useCorrectSource(applicationId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    ApplicationDetailResponse,
    ApiError,
    CorrectSourceInput & { readonly label: string },
    { readonly previous: ApplicationDetailResponse | undefined }
  >({
    mutationFn: ({ source, version }) => apiPost<ApplicationDetailResponse>(`/applications/${applicationId}/source`, { source, version }),
    onMutate: async ({ label }) => {
      await queryClient.cancelQueries({ queryKey: applicationKey(applicationId) });
      const previous = queryClient.getQueryData<ApplicationDetailResponse>(applicationKey(applicationId));
      if (previous) {
        queryClient.setQueryData<ApplicationDetailResponse>(applicationKey(applicationId), {
          ...previous,
          application: { ...previous.application, sourceLabel: label, needsSourceReview: false },
          can: { correctSource: false },
        });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(applicationKey(applicationId), context.previous);
    },
    onSuccess: (detail) => {
      queryClient.setQueryData(applicationKey(applicationId), detail);
    },
  });
}

export function useCreateCandidate() {
  const queryClient = useQueryClient();
  return useMutation<ManualCandidateResult, ApiError, ManualCandidateRequest>({
    mutationFn: (input) => apiPost<ManualCandidateResult>('/candidates', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
    },
  });
}

export function useUploadCv(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation<CandidateProfile, ApiError, { readonly file: File; readonly applicationId: string | null }>({
    mutationFn: async ({ file, applicationId }) => {
      const body = new FormData();
      body.append('cv', file);
      const result = await request<CandidateProfile>(`/candidates/${candidateId}/cv`, {
        method: 'POST',
        body,
        ...(applicationId ? { query: { applicationId } } : {}),
      });
      return result.data;
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(candidateKey(candidateId), profile);
    },
  });
}

/** Where a document opens: the API streams it, the browser shows a PDF in a new tab. */
export function documentUrl(documentId: string): string {
  return `/api/v1/documents/${documentId}`;
}


// --- S29 (Q4): recording a withdrawal on the candidate's behalf ----------------------------------

export interface WithdrawalReasonOption {
  readonly code: string;
  readonly label: string;
  /** WD-PROCESS / WD-EXPERIENCE: the HR Lead is told (N-61). */
  readonly isAccountability: boolean;
}

const WITHDRAWAL_REASONS_KEY = ['withdrawal-reasons'] as const;

export function useWithdrawalReasons(isEnabled: boolean): UseQueryResult<readonly WithdrawalReasonOption[], ApiError> {
  return useQuery({ queryKey: WITHDRAWAL_REASONS_KEY, queryFn: () => apiGet<readonly WithdrawalReasonOption[]>('/withdrawal-reasons'), enabled: isEnabled, staleTime: 5 * 60_000 });
}

export interface RecordWithdrawalVariables {
  readonly applicationId: string;
  readonly reasonCode: string;
  readonly note?: string;
  readonly version: number;
}

/**
 * Act first (§5a rule 1): the application reads Withdrawn at once, the server confirms, and a
 * refusal puts it back with the reason. Lists refresh from the server afterwards.
 */
export function useRecordWithdrawal() {
  const queryClient = useQueryClient();
  return useMutation<{ readonly stage: string; readonly version: number }, ApiError, RecordWithdrawalVariables, { readonly previous: ApplicationDetailResponse | undefined }>({
    mutationFn: ({ applicationId, ...body }) => apiPost(`/applications/${applicationId}/withdraw`, body),
    onMutate: async ({ applicationId }) => {
      await queryClient.cancelQueries({ queryKey: applicationKey(applicationId) });
      const previous = queryClient.getQueryData<ApplicationDetailResponse>(applicationKey(applicationId));
      if (previous) queryClient.setQueryData<ApplicationDetailResponse>(applicationKey(applicationId), { ...previous, application: { ...previous.application, stage: 'withdrawn' } });
      return { previous };
    },
    onError: (_error, { applicationId }, context) => {
      if (context?.previous) queryClient.setQueryData(applicationKey(applicationId), context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: APPLICATIONS_KEY });
      await queryClient.invalidateQueries({ queryKey: CANDIDATES_KEY });
    },
  });
}

// --- S30 (Q17): keep permanently, past the retention period --------------------------------------

/** Shows as kept at once; the server's profile replaces it, a refusal restores it (§5a rule 1). */
export function useKeepCandidate(candidateId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { readonly keep: boolean; readonly reason?: string; readonly version: number }, { readonly previous: CandidateProfile | undefined }>({
    mutationFn: ({ keep, reason, version }) => (keep ? apiPost<void>(`/candidates/${candidateId}/keep`, { reason, version }) : apiPost<void>(`/candidates/${candidateId}/keep/release`, { version })),
    onMutate: async ({ keep, reason }) => {
      await queryClient.cancelQueries({ queryKey: candidateKey(candidateId) });
      const previous = queryClient.getQueryData<CandidateProfile>(candidateKey(candidateId));
      if (previous && keep) {
        queryClient.setQueryData<CandidateProfile>(candidateKey(candidateId), { ...previous, candidate: { ...previous.candidate, retention: { kind: 'kept', reason: reason ?? null, setAt: new Date().toISOString(), setByName: null } } });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(candidateKey(candidateId), context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: candidateKey(candidateId) }),
  });
}
